// Journey de leads de BBTS. Fuente única de verdad para las etapas del lead,
// el mapeo desde el campo `status` legacy y el cálculo de urgencia de seguimiento.
// Ver docs/journey-leads-y-seguimiento.md.

export const LEAD_STAGES = [
  {
    id: 'nuevo',
    label: 'Nuevo',
    description: 'Capturado, sin contactar.',
    badge: 'bg-slate-100 text-slate-700',
    dot: 'bg-slate-400',
  },
  {
    id: 'contactado',
    label: 'Contactado',
    description: 'Ya se habló, cualificado, buscando la cita.',
    badge: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-500',
  },
  {
    id: 'descubrimiento',
    label: 'Descubrimiento',
    description: 'Cita agendada o realizada — se levanta el proceso del negocio.',
    badge: 'bg-violet-100 text-violet-700',
    dot: 'bg-violet-500',
  },
  {
    id: 'cotizacion',
    label: 'Cotización',
    description: 'Propuesta con alcance y precio enviada.',
    badge: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
  },
  {
    id: 'cliente',
    label: 'Cliente',
    description: 'Cerró.',
    badge: 'bg-emerald-100 text-emerald-700',
    dot: 'bg-emerald-500',
  },
  {
    id: 'inactivo',
    label: 'Inactivo',
    description: 'No cerró — recibe promociones por email.',
    badge: 'bg-gray-100 text-gray-600',
    dot: 'bg-gray-400',
  },
];

// Etapas en las que el lead sigue en juego (excluye cerrado y descartado).
export const ACTIVE_STAGE_IDS = ['nuevo', 'contactado', 'descubrimiento', 'cotizacion'];

// Los leads existentes traen `status` legacy; se deriva la etapa sin migrar la base.
const LEGACY_STATUS_TO_STAGE = {
  nuevo: 'nuevo',
  en_proceso: 'contactado',
  convertido: 'cliente',
  inactivo: 'inactivo',
};

const STAGE_BY_ID = LEAD_STAGES.reduce((acc, stage) => {
  acc[stage.id] = stage;
  return acc;
}, {});

export const getLeadStageId = (lead = {}) => {
  const explicit = String(lead.stage || '').trim().toLowerCase();
  if (STAGE_BY_ID[explicit]) return explicit;

  const legacy = String(lead.status || '').trim().toLowerCase();
  return LEGACY_STATUS_TO_STAGE[legacy] || 'nuevo';
};

export const getLeadStage = (lead = {}) => STAGE_BY_ID[getLeadStageId(lead)] || LEAD_STAGES[0];

export const getStageById = (stageId) => STAGE_BY_ID[String(stageId || '').trim().toLowerCase()] || null;

export const getNextStage = (stageId) => {
  const index = LEAD_STAGES.findIndex((stage) => stage.id === stageId);
  // No hay "siguiente" desde cliente ni desde inactivo: son estados finales.
  if (index === -1 || index >= LEAD_STAGES.length - 2) return null;
  return LEAD_STAGES[index + 1];
};

// Copy compartido con el backend (calendar-functions/utils/productLines.js) para
// que las plantillas de campaña no vuelvan a asumir que BookMe es lo único que
// vende BBTS. Si cambia el pitch, actualizar ambos lados.
export const PRODUCT_LINES = [
  {
    id: 'erp',
    label: 'CRM/ERP a la medida',
    badge: 'bg-indigo-100 text-indigo-700',
    shortPitch: 'CRM, proyectos, HR y facturación construido a la medida de tu empresa',
    demoUrl: 'https://bbtspr.com/erp-demo',
  },
  {
    id: 'restaurante',
    label: 'Restaurantes',
    badge: 'bg-rose-100 text-rose-700',
    shortPitch: 'POS/Clover, menú digital, reservas y delivery en un solo sistema',
    demoUrl: 'https://bbtspr.com/restaurante-demo',
  },
];

export const getProductLine = (lead = {}) =>
  PRODUCT_LINES.find((line) => line.id === String(lead.productLine || '').trim().toLowerCase()) || null;

// --- Seguimiento -----------------------------------------------------------

// Firestore Timestamp, Date o string ISO -> Date. null si no hay valor usable.
export const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

export const daysSince = (value) => {
  const date = toDate(value);
  if (!date) return null;
  const diffMs = startOfDay(new Date()).getTime() - startOfDay(date).getTime();
  return Math.floor(diffMs / 86400000);
};

export const FOLLOW_UP_URGENCY = {
  vencido: { id: 'vencido', label: 'Vencido', badge: 'bg-red-100 text-red-700', order: 0 },
  hoy: { id: 'hoy', label: 'Hoy', badge: 'bg-amber-100 text-amber-800', order: 1 },
  frio: { id: 'frio', label: 'Frío', badge: 'bg-orange-100 text-orange-700', order: 2 },
  programado: { id: 'programado', label: 'Programado', badge: 'bg-blue-50 text-blue-700', order: 3 },
  sin_programar: { id: 'sin_programar', label: 'Sin programar', badge: 'bg-gray-100 text-gray-600', order: 4 },
  cerrado: { id: 'cerrado', label: '—', badge: 'bg-gray-50 text-gray-400', order: 5 },
};

// Días sin actividad a partir de los cuales un lead activo se considera frío.
const COLD_DAYS_NEW = 3;
const COLD_DAYS_ACTIVE = 7;

export const getFollowUpUrgency = (lead = {}) => {
  const stageId = getLeadStageId(lead);
  if (!ACTIVE_STAGE_IDS.includes(stageId)) {
    return FOLLOW_UP_URGENCY.cerrado;
  }

  const nextFollowUp = toDate(lead.nextFollowUpAt);
  if (nextFollowUp) {
    const today = startOfDay(new Date()).getTime();
    const scheduled = startOfDay(nextFollowUp).getTime();
    if (scheduled < today) return FOLLOW_UP_URGENCY.vencido;
    if (scheduled === today) return FOLLOW_UP_URGENCY.hoy;
    return FOLLOW_UP_URGENCY.programado;
  }

  // Sin próximo contacto agendado: se evalúa si lleva demasiado tiempo quieto.
  const lastTouch = lead.lastContactAt || lead.updatedAt || lead.createdAt;
  const idleDays = daysSince(lastTouch);
  const threshold = stageId === 'nuevo' ? COLD_DAYS_NEW : COLD_DAYS_ACTIVE;
  if (idleDays !== null && idleDays >= threshold) {
    return FOLLOW_UP_URGENCY.frio;
  }

  return FOLLOW_UP_URGENCY.sin_programar;
};

export const formatFollowUpDate = (value) => {
  const date = toDate(value);
  if (!date) return null;

  const days = daysSince(value);
  if (days === 0) return 'Hoy';
  if (days === 1) return 'Ayer';
  if (days === -1) return 'Mañana';
  if (days > 1) return `Hace ${days} días`;
  if (days < -1) return `En ${Math.abs(days)} días`;
  return date.toLocaleDateString('es-PR');
};
